import { useEffect, useRef, useState } from "react";
import { useImmer } from "use-immer";
import SimpleReactValidator from "simple-react-validator";
import {
  getTenantContactSettingsApi,
  updateTenantContactSettingsApi,
} from "../apis/contactApis";
import { errorMessage, successMessage } from "../../../utils";

export const useContactConfig = () => {
  const [, setForceUpdate] = useState(0);
  const [state, setState] = useImmer({
    formData: {
      support_phone: "",
      support_email: "",
      replay_email: "",
    },
    isLoading: true,
    isSubmitting: false,
    hasChanges: false,
  });

  const formValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: setForceUpdate },
    }),
  );

  useEffect(() => {
    fetchContactSettings();
  }, []);

  const fetchContactSettings = async () => {
    try {
      setState((draft) => {
        draft.isLoading = true;
      });

      const response = await getTenantContactSettingsApi();

      if (response?.success) {
        setState((draft) => {
          draft.formData = response.data;
          draft.hasChanges = false;
        });
      } else {
        throw new Error(
          response.message || "Failed to fetch tenant contact settings",
        );
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error?.message ||
        "Failed to fetch tenant contact settings";
      errorMessage(message);
    } finally {
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const updateContactSettings = async (payload) => {
    try {
      setState((draft) => {
        draft.isSubmitting = true;
      });

      const response = await updateTenantContactSettingsApi(payload);

      if (response?.success) {
        setState((draft) => {
          draft.formData = response.data;
          draft.hasChanges = false;
        });
        successMessage(response.message);
      } else {
        throw new Error(
          response.message || "Failed to update tenant contact settings",
        );
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error?.message ||
        "Failed to update tenant contact settings";
      errorMessage(message);
    } finally {
      setState((draft) => {
        draft.isSubmitting = false;
      });
    }
  };

  const onChange = (event) => {
    const { name, value } = event.target;
    setState((draft) => {
      draft.formData[name] = value;
      draft.hasChanges = true;
    });
  };

  const onSubmit = () => {
    if (formValidator.current.allValid()) {
      updateContactSettings(state.formData);
    } else {
      formValidator.current.showMessages();
      setForceUpdate((previous) => previous + 1);
    }
  };

  return { state, formValidator, onChange, onSubmit };
};
